import React,{useEffect,useMemo,useState} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {legalVaultService} from '../../services/legalVaultService';
import {exportService} from '../../services/exportService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiSearch,FiFileText,FiAlertCircle,FiRefreshCcw,FiArrowUpRight,FiCalendar,FiBriefcase,FiBookOpen,FiExternalLink,FiEye,FiDownload,FiLink,FiX,FiCheckCircle,FiLoader}=FiIcons;

const formatAustralianDate=(value)=> {
  if (!value) return '';
  const parsed=new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'});
};

const POLICY_PATTERN=/(privacy|policy|policies|data sharing|refund|cancellation|disclaimer|cookie|data protection)/i;

const hubSectionForDocument=(doc)=> POLICY_PATTERN.test(`${doc?.type || ''} ${doc?.title || ''}`) ? 'policies' : 'contracts';

const getDocumentRoute=(doc)=> doc.directVersionRoute && doc.version?.id ? `/legal/version/${doc.version.id}` : `/legal/${doc.slug}`;

const getPermanentVersionUrl=(doc)=> doc.version?.id ? `${window.location.origin}/#/legal/version/${doc.version.id}` : '';

const buildPreviewData=(doc)=> ({
  document:{
    title:doc.title,
    type:doc.type,
    version:doc.currentVersion || doc.version?.versionNumber || 'Current',
    status:doc.version?.status || 'Published',
    effectiveDate:doc.version?.effectiveDate || '',
    publishedDate:doc.version?.publishedDate || doc.version?.publishedAt || '',
    contentHash:doc.version?.contentHash || '',
  },
  brand:doc.brand || {},
  content:doc.version?.content || doc.version?.contentMarkdown || '',
});

const copyText=async(value)=> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch (clipboardError) {
      console.warn('Clipboard API unavailable; using copy fallback.',clipboardError);
    }
  }
  const input=document.createElement('textarea');
  input.value=value;
  input.setAttribute('readonly','');
  input.style.position='fixed';
  input.style.opacity='0';
  document.body.appendChild(input);
  input.select();
  const copied=document.execCommand('copy');
  document.body.removeChild(input);
  if (!copied) throw new Error('Copy is not available in this browser.');
};

const PUBLIC_DOCUMENT_TYPE_LABELS={
  service_terms:'Service Terms & Conditions',
  website_terms:'Website Design Terms',
  software_terms:'Software Application Terms',
  saas_terms:'SaaS Terms',
  support_policy:'Support & Maintenance Policies',
  licence_terms:'Licence Terms',
  privacy_policy:'Privacy Policies',
  data_sharing_policy:'Data Sharing Policies',
  refund_policy:'Refund and Cancellation Policies',
  disclaimer:'Disclaimers',
  custom:'Custom',
};

const mapFallbackPublicDocument=(doc,version)=> ({
  ...doc,
  type:PUBLIC_DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType,
  status:'Published',
  visibility:'Public',
  tags:Array.isArray(doc.tags) ? doc.tags.join(',') : doc.tags,
  version:{
    ...version,
    status:'Published',
    effectiveDate:version.effectiveDate || '',
    publishedDate:version.publishedAt || '',
    content:version.content || version.contentMarkdown || '',
  },
  directVersionRoute:true,
});

export default function PublicHub() {
  const [documents,setDocuments]=useState([]);
  const [searchTerm,setSearchTerm]=useState('');
  const [activeTab,setActiveTab]=useState('contracts');
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const [preview,setPreview]=useState(null);
  const [downloadingId,setDownloadingId]=useState(null);
  const [toast,setToast]=useState(null);

  const loadPublicDocuments=async()=> {
    setLoading(true);
    setError(null);
    try {
      const primaryDocs=await Promise.race([
        legalVaultService.getPublicDocuments(),
        new Promise((_,reject)=> setTimeout(()=> reject(new Error('The Contract & Policy Hub could not be loaded. Please try again.')),15000)),
      ]);
      const resolvedDocs=Array.isArray(primaryDocs) ? [...primaryDocs] : [];

      // Defensive fallback: Published + Public is the source of truth for hub visibility.
      // Recover only records omitted because public-link linkage is missing/stale;
      // explicitly disabled or expired public links remain respected.
      try {
        const client=await legalVaultService.requirePocketBase('getPublicHubPublishedFallback');
        const [publishedDocs,publicLinks]=await Promise.all([
          client.collection('legal_documents').getFullList({
            filter:'visibility="public" && status="published"',
            sort:'-updatedAt',
          }),
          client.collection('legal_public_links').getFullList({}),
        ]);
        const now=new Date().toISOString();
        const existingIds=new Set(resolvedDocs.map((doc)=> doc.id));

        for (const publishedDoc of publishedDocs) {
          if (existingIds.has(publishedDoc.id)) continue;
          const documentLinks=publicLinks.filter((link)=> link.documentId===publishedDoc.id);
          const explicitlyDisabled=documentLinks.some((link)=> link.isActive===false && link.disabledReason==='Disabled by administrator.');
          const explicitlyExpired=documentLinks.some((link)=> Boolean(link.expiresAt) && link.expiresAt<=now);
          if (explicitlyDisabled || explicitlyExpired) continue;

          let versions=await client.collection('legal_versions').getFullList({
            filter:`documentId="${publishedDoc.id}" && status="published" && isCurrent=true`,
            sort:'-publishedAt',
          });
          if (versions.length===0) {
            versions=await client.collection('legal_versions').getFullList({
              filter:`documentId="${publishedDoc.id}" && status="published"`,
              sort:'-publishedAt',
            });
          }
          if (versions.length===0) continue;
          resolvedDocs.push(mapFallbackPublicDocument(publishedDoc,versions[0]));
          existingIds.add(publishedDoc.id);
        }
      } catch (fallbackError) {
        console.warn('Published-document hub fallback skipped:',fallbackError);
      }

      setDocuments(resolvedDocs);
    } catch (loadError) {
      console.error('Unable to load public Contract & Policy Hub:',loadError);
      setDocuments([]);
      setError(loadError.message || 'The Contract & Policy Hub could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=> {
    loadPublicDocuments();
  },[]);

  useEffect(()=> {
    if (!preview) return undefined;
    const previousOverflow=document.body.style.overflow;
    const handleKeyDown=(event)=> {
      if (event.key==='Escape') setPreview(null);
    };
    document.body.style.overflow='hidden';
    window.addEventListener('keydown',handleKeyDown);
    return ()=> {
      document.body.style.overflow=previousOverflow;
      window.removeEventListener('keydown',handleKeyDown);
    };
  },[preview]);

  const showToast=(message)=> {
    setToast(message);
    window.setTimeout(()=> setToast(null),2400);
  };

  const prepareDocumentForAction=async(doc)=> {
    const brand=doc.brand || await legalVaultService.getBrandingProfileForDocument(doc);
    return {
      ...doc,
      brand:brand || {},
      version:{
        ...doc.version,
        content:doc.version?.content || doc.version?.contentMarkdown || '',
        publishedDate:doc.version?.publishedDate || doc.version?.publishedAt || '',
      },
    };
  };

  const handlePreview=async(doc)=> {
    try {
      const prepared=await prepareDocumentForAction(doc);
      const html=exportService.generatePrintableHTML(buildPreviewData(prepared));
      setPreview({doc:prepared,html});
    } catch (previewError) {
      console.error('Preview failed:',previewError);
      showToast('Preview is temporarily unavailable.');
    }
  };

  const handleDownload=async(doc)=> {
    if (downloadingId) return;
    setDownloadingId(doc.id);
    try {
      const prepared=await prepareDocumentForAction(doc);
      await exportService.exportDocument(prepared,'PDF');
      showToast('PDF downloaded.');
    } catch (downloadError) {
      console.error('PDF download failed:',downloadError);
      showToast('PDF download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCopyPermanentLink=async(doc)=> {
    try {
      const permanentUrl=getPermanentVersionUrl(doc);
      if (!permanentUrl) throw new Error('Permanent version link is unavailable.');
      await copyText(permanentUrl);
      showToast('Permanent link copied.');
    } catch (copyError) {
      console.error('Permanent-link copy failed:',copyError);
      showToast(copyError.message || 'Unable to copy the permanent link.');
    }
  };

  const counts=useMemo(()=> documents.reduce((result,doc)=> {
    const section=hubSectionForDocument(doc);
    result[section]+=1;
    return result;
  },{contracts:0,policies:0}),[documents]);

  const filteredDocs=useMemo(()=> {
    const query=searchTerm.trim().toLowerCase();
    return [...documents]
      .filter((doc)=> hubSectionForDocument(doc)===activeTab)
      .filter((doc)=> {
        if (!query) return true;
        return [doc.title,doc.type,doc.tags]
          .filter(Boolean)
          .some((value)=> String(value).toLowerCase().includes(query));
      })
      .sort((a,b)=> Number(Boolean(b.isFeatured))-Number(Boolean(a.isFeatured)) || a.title.localeCompare(b.title));
  },[documents,searchTerm,activeTab]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="w-11 h-11 rounded-full border-[3px] border-slate-200 border-t-[#8c6a2f] animate-spin mx-auto mb-5" />
        <p className="text-sm font-medium text-slate-600">Loading BPA Solutions contracts and policies…</p>
      </div>
    );
  }

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-12">
      <section className="relative overflow-hidden rounded-3xl bg-[#0f172a] text-white border border-slate-800 shadow-2xl shadow-slate-900/10 mb-7">
        <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-[#8c6a2f]/12 to-transparent pointer-events-none" />
        <div className="relative px-6 sm:px-10 lg:px-12 py-9 sm:py-11">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#e7d39e] mb-4">BPA Solutions</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">Contract & Policy Hub</h1>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-300 max-w-3xl">
              One place to access BPA Solutions contracts, master terms and conditions, service terms, privacy policies and other published documents used across client engagements.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-400 max-w-3xl">
              Statements of Work and other client documents may refer to agreements published here. Open the relevant document to view its published details and download a polished PDF copy.
            </p>
          </div>

          <div className="mt-7 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 max-w-4xl">
            <p className="text-sm text-slate-200 leading-relaxed">
              <strong className="text-white">BPA Solutions</strong> (Business Process Automation Solutions) is a trading name of <strong className="text-white">Anish Malhotra Pty Ltd</strong>.
            </p>
          </div>
        </div>
      </section>

      {error && (
        <div className="mb-7 bg-white border border-red-200 rounded-2xl p-6 sm:p-8 shadow-sm text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiAlertCircle} className="text-2xl text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Contracts and policies are temporarily unavailable</h3>
          <p className="text-sm text-slate-600 mb-5 max-w-xl mx-auto">{error}</p>
          <button onClick={loadPublicDocuments} className="inline-flex items-center px-4 py-2.5 bg-[#0f172a] text-white rounded-xl text-sm font-semibold hover:bg-slate-800 transition-colors">
            <SafeIcon icon={FiRefreshCcw} className="mr-2" /> Retry
          </button>
        </div>
      )}

      {!error && (
        <>
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6 overflow-hidden">
            <div className="flex border-b border-slate-200 bg-slate-50/80">
              <button
                type="button"
                onClick={()=> setActiveTab('contracts')}
                className={`flex-1 sm:flex-none min-w-[160px] px-5 py-4 text-sm font-semibold border-b-2 transition-colors ${activeTab==='contracts' ? 'border-[#8c6a2f] text-[#705124] bg-white' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
              >
                <span className="inline-flex items-center gap-2"><SafeIcon icon={FiBriefcase} /> Contracts <span className="text-xs text-slate-400">{counts.contracts}</span></span>
              </button>
              <button
                type="button"
                onClick={()=> setActiveTab('policies')}
                className={`flex-1 sm:flex-none min-w-[160px] px-5 py-4 text-sm font-semibold border-b-2 transition-colors ${activeTab==='policies' ? 'border-[#8c6a2f] text-[#705124] bg-white' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
              >
                <span className="inline-flex items-center gap-2"><SafeIcon icon={FiBookOpen} /> Policies <span className="text-xs text-slate-400">{counts.policies}</span></span>
              </button>
            </div>

            <div className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="relative flex-1 max-w-2xl">
                <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  placeholder={`Search ${activeTab}…`}
                  value={searchTerm}
                  onChange={(event)=> setSearchTerm(event.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:bg-white focus:border-[#8c6a2f]/50 focus:ring-4 focus:ring-[#8c6a2f]/10 transition-all"
                />
              </div>
              <p className="text-xs text-slate-500 lg:ml-auto flex items-center gap-2">
                <SafeIcon icon={FiExternalLink} /> Documents open in a new tab
              </p>
            </div>
          </section>

          {filteredDocs.length===0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={activeTab==='contracts' ? FiBriefcase : FiBookOpen} className="text-xl text-slate-400" />
              </div>
              <h3 className="text-base font-semibold text-slate-800">No matching {activeTab}</h3>
              <p className="text-sm text-slate-500 mt-1">Try a different search term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
              {filteredDocs.map((doc)=> {
                const versionNumber=doc.version?.versionNumber || 'Current';
                const effectiveDate=formatAustralianDate(doc.version?.effectiveDate);
                return (
                  <article
                    key={doc.id}
                    className="group bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:shadow-slate-900/7 hover:border-[#8c6a2f]/35 transition-all duration-200 overflow-hidden"
                  >
                    <Link
                      to={getDocumentRoute(doc)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      title="Open document in a new tab"
                    >
                      <div className="h-0.5 bg-[#b38a45] opacity-80" />
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-600 group-hover:bg-[#f7f2e7] group-hover:text-[#8c6a2f] transition-colors">
                            <SafeIcon icon={activeTab==='contracts' ? FiBriefcase : FiBookOpen} />
                          </div>
                          <SafeIcon icon={FiArrowUpRight} className="text-slate-300 group-hover:text-[#8c6a2f] transition-colors" />
                        </div>

                        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#8c6a2f] mb-1.5 line-clamp-1">{doc.type}</p>
                        <h2 className="text-[15px] font-semibold leading-snug text-slate-900 group-hover:text-[#705124] transition-colors line-clamp-3">{doc.title}</h2>

                        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 text-[11px] text-slate-500">
                          <span className="font-medium">{versionNumber}</span>
                          <span className="inline-flex items-center gap-1"><SafeIcon icon={FiCalendar} /> {effectiveDate || 'Published'}</span>
                        </div>
                      </div>
                    </Link>

                    <div className="grid grid-cols-3 border-t border-slate-100 bg-slate-50/70">
                      <button
                        type="button"
                        onClick={()=> handlePreview(doc)}
                        className="min-h-11 inline-flex items-center justify-center gap-1.5 px-2 text-[11px] font-semibold text-slate-600 hover:text-[#705124] hover:bg-[#f7f2e7] transition-colors border-r border-slate-100"
                        title="Quick preview"
                        aria-label={`Preview ${doc.title}`}
                      >
                        <SafeIcon icon={FiEye} /> <span>Preview</span>
                      </button>
                      <button
                        type="button"
                        onClick={()=> handleDownload(doc)}
                        disabled={downloadingId===doc.id}
                        className="min-h-11 inline-flex items-center justify-center gap-1.5 px-2 text-[11px] font-semibold text-slate-600 hover:text-[#705124] hover:bg-[#f7f2e7] transition-colors border-r border-slate-100 disabled:opacity-50 disabled:cursor-wait"
                        title="Download PDF"
                        aria-label={`Download ${doc.title} as PDF`}
                      >
                        <SafeIcon icon={downloadingId===doc.id ? FiLoader : FiDownload} className={downloadingId===doc.id ? 'animate-spin' : ''} /> <span>PDF</span>
                      </button>
                      <button
                        type="button"
                        onClick={()=> handleCopyPermanentLink(doc)}
                        className="min-h-11 inline-flex items-center justify-center gap-1.5 px-2 text-[11px] font-semibold text-slate-600 hover:text-[#705124] hover:bg-[#f7f2e7] transition-colors"
                        title="Copy permanent version link"
                        aria-label={`Copy permanent link for ${doc.title}`}
                      >
                        <SafeIcon icon={FiLink} /> <span>Link</span>
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <div className="mt-6 text-center text-xs text-slate-500">
            <SafeIcon icon={FiFileText} className="inline mr-1.5" /> Preview, download the current PDF, or copy an immutable permanent version link directly from each card.
          </div>
        </>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[1400] bg-slate-950/70 backdrop-blur-sm p-2 sm:p-5 flex items-center justify-center"
          role="presentation"
          onMouseDown={(event)=> {
            if (event.target===event.currentTarget) setPreview(null);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-label={`Preview ${preview.doc.title}`}
            className="w-full max-w-6xl h-[94vh] sm:h-[90vh] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
          >
            <header className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-slate-200 bg-white">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#8c6a2f]">Document preview</p>
                <h2 className="text-sm sm:text-base font-semibold text-slate-900 truncate">{preview.doc.title}</h2>
                <p className="text-xs text-slate-500">{preview.doc.version?.versionNumber || 'Current'} · Published record</p>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <button
                  type="button"
                  onClick={()=> handleDownload(preview.doc)}
                  disabled={downloadingId===preview.doc.id}
                  className="min-h-10 px-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <SafeIcon icon={downloadingId===preview.doc.id ? FiLoader : FiDownload} className={downloadingId===preview.doc.id ? 'animate-spin' : ''} /> Download PDF
                </button>
                <button
                  type="button"
                  onClick={()=> handleCopyPermanentLink(preview.doc)}
                  className="min-h-10 px-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <SafeIcon icon={FiLink} /> Copy permanent link
                </button>
              </div>
              <button
                type="button"
                onClick={()=> setPreview(null)}
                className="w-10 h-10 rounded-lg border border-slate-200 inline-flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                aria-label="Close preview"
                title="Close preview"
              >
                <SafeIcon icon={FiX} />
              </button>
            </header>

            <div className="sm:hidden grid grid-cols-2 border-b border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={()=> handleDownload(preview.doc)}
                disabled={downloadingId===preview.doc.id}
                className="min-h-11 inline-flex items-center justify-center gap-2 text-xs font-semibold text-slate-700 border-r border-slate-200 disabled:opacity-50"
              >
                <SafeIcon icon={downloadingId===preview.doc.id ? FiLoader : FiDownload} className={downloadingId===preview.doc.id ? 'animate-spin' : ''} /> Download PDF
              </button>
              <button
                type="button"
                onClick={()=> handleCopyPermanentLink(preview.doc)}
                className="min-h-11 inline-flex items-center justify-center gap-2 text-xs font-semibold text-slate-700"
              >
                <SafeIcon icon={FiLink} /> Copy link
              </button>
            </div>

            <iframe
              title={`Preview of ${preview.doc.title}`}
              srcDoc={preview.html}
              className="w-full flex-1 bg-white"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
            />
          </section>
        </div>
      )}

      {toast && (
        <div className="fixed z-[1500] right-4 bottom-4 max-w-sm rounded-xl bg-[#0f172a] text-white shadow-xl px-4 py-3 text-sm font-medium flex items-center gap-2" role="status">
          <SafeIcon icon={FiCheckCircle} className="text-[#e7d39e]" />
          <span>{toast}</span>
        </div>
      )}
    </motion.div>
  );
}